import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Plug, CheckCircle2, XCircle, CreditCard } from "lucide-react";

const PROVIDERS = {
  cod: { label: "Manual / Cash on Delivery only", fields: [] },
  cashfree: { label: "Cashfree", fields: [["app_id", "App ID"], ["secret_key", "Secret Key", "secret"], ["mode", "Mode", "mode"]] },
  razorpay: { label: "Razorpay", fields: [["key_id", "Key ID"], ["key_secret", "Key Secret", "secret"], ["webhook_secret", "Webhook Secret", "secret"], ["mode", "Mode", "mode"]] },
  payu: { label: "PayU", fields: [["merchant_id", "Merchant ID"], ["api_key", "API Key", "secret"], ["salt", "Salt", "secret"], ["client_id", "Client ID"], ["client_secret", "Client Secret", "secret"], ["mode", "Mode", "mode"]] },
  stripe: { label: "Stripe", fields: [["publishable_key", "Publishable Key"], ["secret_key", "Secret Key", "secret"], ["webhook_secret", "Webhook Secret", "secret"], ["mode", "Mode", "mode"]] },
};

export default function PaymentIntegration() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { api.get("/admin/payment-config").then((r) => setCfg(r.data)); }, []);

  const setProv = (prov, field, val) => setCfg((c) => ({ ...c, [prov]: { ...c[prov], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/payment-config", cfg); setCfg(data); toast.success("Payment settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      await api.put("/admin/payment-config", cfg); // ensure latest saved before testing
      const { data } = await api.post("/admin/payment-config/test", { provider: cfg.provider });
      setTestResult(data);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch { setTestResult({ ok: false, message: "Test request failed" }); toast.error("Test request failed"); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  const provider = cfg.provider || "cod";
  const def = PROVIDERS[provider];

  return (
    <div className="bg-white rounded-2xl soft-shadow p-6 space-y-5" data-testid="payment-integration-section">
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-forest" />
        <h2 className="font-heading text-2xl text-forest">Payment Integration</h2>
      </div>

      {/* Payment Control */}
      <div className="rounded-xl bg-sand p-4 space-y-3">
        <p className="text-sm font-500 text-brown">Payment Control</p>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable Cash on Delivery (COD)</Label>
          <Switch checked={cfg.cod_enabled !== false} onCheckedChange={(v) => setCfg((c) => ({ ...c, cod_enabled: v }))} data-testid="pay-toggle-cod" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable Online Payments</Label>
          <Switch checked={!!cfg.online_enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, online_enabled: v }))} data-testid="pay-toggle-online" />
        </div>
      </div>

      {/* Provider selection */}
      <div>
        <Label className="text-sm mb-1.5 block">Payment Provider</Label>
        <Select value={provider} onValueChange={(v) => { setCfg((c) => ({ ...c, provider: v })); setTestResult(null); }}>
          <SelectTrigger className="rounded-xl bg-sand" data-testid="pay-provider-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PROVIDERS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Provider fields */}
      {def.fields.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {def.fields.map(([key, label, type]) => (
            <div key={key} className={type === "mode" ? "" : "sm:col-span-2"}>
              <Label className="text-sm mb-1 block">{label}</Label>
              {type === "mode" ? (
                <Select value={cfg[provider]?.[key] || "test"} onValueChange={(v) => setProv(provider, key, v)}>
                  <SelectTrigger className="rounded-xl bg-sand" data-testid={`pay-${provider}-mode`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
                </Select>
              ) : (
                <Input type="text" value={cfg[provider]?.[key] || ""}
                  onChange={(e) => setProv(provider, key, e.target.value)}
                  placeholder={label}
                  className="rounded-xl bg-sand font-mono text-sm" data-testid={`pay-${provider}-${key}`} autoComplete="off" spellCheck="false" />
              )}
            </div>
          ))}
        </div>
      )}

      {testResult && (
        <div className={`flex items-start gap-2 text-sm rounded-xl p-3 ${testResult.ok ? "bg-earth/10 text-earth" : "bg-terracotta/10 text-terracotta"}`} data-testid="pay-test-result">
          {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{testResult.message}</span>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="pay-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Payment Settings</>}
        </Button>
        <Button onClick={testConnection} disabled={testing} variant="outline" className="rounded-full border-forest text-forest" data-testid="pay-test-btn">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plug className="w-4 h-4 mr-1.5" /> Test Connection</>}
        </Button>
      </div>
      <p className="text-xs text-brown/50">Credentials are stored securely on the server and are never exposed to the storefront or customers. As Super Admin, values are shown here in plain text so you can review and edit them anytime.</p>
    </div>
  );
}
